/**
 * Bildschirm wach halten, solange Noten offen sind. Native Wake-Lock-API,
 * nach dem Zurückkehren in den Tab neu angefordert. In Home-Bildschirm-Apps
 * älterer iPadOS-Versionen fehlt die API → NoSleep (stummes Video) als Fallback.
 */
type Sentinel = { release: () => Promise<void>; addEventListener: (t: "release", cb: () => void) => void };

export class WakeLock {
  private sentinel: Sentinel | null = null;
  private fallback: { enable: () => Promise<void>; disable: () => void } | null = null;
  private wanted = false;

  constructor() {
    document.addEventListener("visibilitychange", this.onVisible);
  }

  /** Möglichst innerhalb einer Nutzer-Geste aufrufen (für den Fallback nötig). */
  async enable(): Promise<void> {
    this.wanted = true;
    if (await this.requestNative()) return;
    try {
      if (!this.fallback) {
        const { default: NoSleep } = await import("nosleep.js");
        this.fallback = new NoSleep();
      }
      await this.fallback.enable();
    } catch {
      // ohne Geste nicht möglich – beim nächsten Tippen erneut
    }
  }

  private async requestNative(): Promise<boolean> {
    const wakeLock = (navigator as Navigator & { wakeLock?: { request: (t: "screen") => Promise<Sentinel> } })
      .wakeLock;
    if (!wakeLock || document.visibilityState !== "visible") return false;
    try {
      this.sentinel = await wakeLock.request("screen");
      this.sentinel.addEventListener("release", () => (this.sentinel = null));
      return true;
    } catch {
      return false;
    }
  }

  get active(): boolean {
    return Boolean(this.sentinel);
  }

  private onVisible = () => {
    if (this.wanted && document.visibilityState === "visible" && !this.sentinel) void this.requestNative();
  };

  dispose(): void {
    this.wanted = false;
    document.removeEventListener("visibilitychange", this.onVisible);
    void this.sentinel?.release().catch(() => undefined);
    this.sentinel = null;
    this.fallback?.disable();
  }
}
